/**
 * Which QuickBooks account each kind of Rowted money lands on — §F.4's table,
 * as a screen.
 *
 * The keys are fixed, because the code decides which key a document uses: a
 * write-off is always `bad_debt`, a waived fee is always `fee_waived`. What is
 * the tenant's to say is the *name* — one company's "Landscape services" is
 * another's "Sales — maintenance", and both are right.
 *
 * Typed rather than picked from a list, deliberately. Pulling the real chart
 * of accounts needs a live QuickBooks connection, and a screen that could only
 * be filled in after OAuth would mean nobody could set this up before their
 * accountant was in the room. Typing the name they already use works now, and
 * matching it against the real list is what the connection does when it lands.
 */
import { useCallback, useEffect, useState } from 'react'
import { SlideOver } from './overlays.js'
import { Button, SectionLabel, Tag } from './primitives.js'
import { Field } from './fields.js'
import { request } from '../lib/api.js'

interface Mapping {
  kind: string
  label: string
  hint: string
  blocking: boolean
  itemName: string | null
  accountName: string | null
}

export function QuickBooksMapping({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [items, setItems] = useState<Mapping[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const result = await request<{ items: Mapping[] }>('/invoices/settings/quickbooks')
      setItems(result.items)
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be loaded.')
    }
  }, [])

  useEffect(() => { if (open) void load() }, [open, load])

  /*
   * Saved on blur, one row at a time, rather than behind a Save button.
   *
   * Eleven rows of two fields is twenty-two things to get wrong in one go, and
   * a single Save means all twenty-two are unsaved until the last one is
   * right. A row that saves itself means the six you have filled in are safe
   * while you go and ask about the seventh.
   */
  async function save(kind: string, patch: Partial<Mapping>) {
    const current = items.find((item) => item.kind === kind)
    if (!current) return
    const next = { ...current, ...patch }
    if (next.itemName === current.itemName && next.accountName === current.accountName) return

    setSaving(kind)
    try {
      const result = await request<{ items: Mapping[] }>('/invoices/settings/quickbooks', {
        method: 'PUT',
        body: { kind, itemName: next.itemName, accountName: next.accountName },
      })
      setItems(result.items)
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be saved.')
      void load()
    } finally {
      setSaving(null)
    }
  }

  const missing = items.filter((item) => item.blocking && !item.accountName && !item.itemName)

  return (
    <SlideOver open={open} onClose={onClose} title="QuickBooks accounts" width={460}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <div className="flex flex-col gap-4">
        {error && (
          <p className="m-0 rounded-card border border-danger bg-danger-bg p-2 text-cell text-danger">
            {error}
          </p>
        )}

        <p className="m-0 text-cell text-text-muted">
          What each kind of money is called in your books. Type the names you
          already use — nothing is posted to QuickBooks until the connection is
          switched on, and these are what it will post against.
        </p>

        {missing.length > 0 && (
          <div className="rounded-card border border-warn bg-warn-bg p-2.5">
            <p className="m-0 text-cell font-semibold text-warn">
              {missing.length === 1
                ? '1 account is needed before anything can sync'
                : `${missing.length} accounts are needed before anything can sync`}
            </p>
            <p className="m-0 mt-0.5 text-sub text-text-muted">
              {missing.map((item) => item.label).join(', ')}.
            </p>
          </div>
        )}

        {items.map((item) => (
          <section
            key={item.kind}
            /*
             * React's `onBlur` is the bubbling `focusout`, so one handler on
             * the row catches whichever of its two fields was left. The row
             * saves itself; there is no Save button to forget.
             */
            onBlur={() => void save(item.kind, {})}
            className="flex flex-col gap-2 border-t border-line-soft pt-3 first:border-t-0 first:pt-0"
          >
            <div className="flex items-center gap-2">
              <SectionLabel>{item.label}</SectionLabel>
              {item.blocking && !item.accountName && !item.itemName && <Tag tone="warn">Needed</Tag>}
              {saving === item.kind && <span className="text-sub text-text-faint">Saving…</span>}
            </div>
            <p className="m-0 text-sub text-text-muted">{item.hint}</p>
            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Item"
                value={item.itemName ?? ''}
                onChange={(value) => setItems((current) => current.map((entry) =>
                  (entry.kind === item.kind ? { ...entry, itemName: value } : entry)))}
                placeholder="Landscape services"
              />
              <Field
                label="Account"
                value={item.accountName ?? ''}
                onChange={(value) => setItems((current) => current.map((entry) =>
                  (entry.kind === item.kind ? { ...entry, accountName: value } : entry)))}
                placeholder="Sales — maintenance"
              />
            </div>
          </section>
        ))}
      </div>
    </SlideOver>
  )
}
